import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import notify, { MESSAGES } from '../../../utils/notify';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import { unwrapProcurement } from './normalize';
import { procurementCacheKey } from './cacheKey';

export { procurementCacheKey };

const useProcurementDetail = (procurementId, { enabled = true } = {}) => {
  const [procurement, setProcurement] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(enabled && procurementId));
  const [error, setError] = useState(null);

  const fetchProcurement = useCallback(async () => {
    if (!procurementId || !enabled) return;

    const token = getAuthToken();
    if (!token) {
      notify.error(MESSAGES.authRequired);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${api.API_URL}/procurement/${procurementId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );
      setProcurement(unwrapProcurement(response));
    } catch (err) {
      const message =
        typeof err === 'string' ? err : 'Failed to load procurement detail';
      setError(message);
      notify.apiError(err, 'load procurement detail');
    } finally {
      setIsLoading(false);
    }
  }, [procurementId, enabled]);

  const refresh = useCallback(() => fetchProcurement(), [fetchProcurement]);

  useEffect(() => {
    if (!enabled || !procurementId) return;
    fetchProcurement();
  }, [enabled, procurementId, fetchProcurement]);

  return {
    procurement: enabled ? procurement : null,
    isLoading: enabled ? isLoading : false,
    error,
    refresh,
  };
};

export default useProcurementDetail;
